import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EASE_OUT } from '../../lib/motion';
import SettingsSubTabs, {
  useSettingsSection,
  type SubTabSection,
} from './SettingsSubTabs';
import { useReconciliation } from '../../services/billing';
import { usePermissions } from '../../hooks/usePermissions';
import { PERM } from '../../auth/permissions';
import BillingSettings from './BillingSettings';
import TierManagement from './TierManagement';
import { RevolutProvider } from '../../contexts/RevolutContext';

const SECTIONS: SubTabSection[] = [
  {
    id: 'billing',
    label: 'Subscription',
    // Not 'invoices': the screen lists payments, and Revolut issues the
    // invoices. Promising a document we do not show is worse than not
    // mentioning it (my own copy, corrected 2026-09-28).
    hint: 'What you are on today, your payment method and your payment history',
  },
  {
    id: 'plan',
    label: 'Plans',
    hint: 'Compare what each plan includes, and change yours',
  },
];

/**
 * Settings → Billing & Plan.
 *
 * Subscription/payments and the plan grid used to be one long stack; they are
 * two separate errands, so they are two sub-tabs (Aleix, 2026-09-11). Each
 * child owns its own data fetching; this component arranges them and provides
 * the Revolut checkout context the subscribe/change flow needs.
 */
const BillingAndPlanSettings: React.FC = () => {
  // The reconciliation banner and the upgrade CTAs deep-link to #tier-plans,
  // which lives in the Plan section — honour that rather than landing them on
  // Billing with nothing to scroll to.
  const { hash } = useLocation();
  const { can } = usePermissions();
  // An open downgrade reconciliation is a decision with a deadline and its
  // chooser lives in Plan — never open on Billing and hide it.
  const { data: reconciliation } = useReconciliation(can(PERM.BILLING_MANAGE));
  const [current, select] = useSettingsSection(
    'billing',
    SECTIONS,
    hash === '#tier-plans' || reconciliation?.open ? 'plan' : undefined
  );

  return (
    <RevolutProvider>
      <SettingsSubTabs
        ariaLabel="Billing sections"
        sections={SECTIONS}
        current={current}
        onSelect={select}
        pillId="billing-section-pill"
      />

      <motion.div
        key={current}
        role="tabpanel"
        id={`section-panel-${current}`}
        aria-labelledby={`section-tab-${current}`}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15, ease: EASE_OUT }}
      >
        {current === 'billing' ? <BillingSettings /> : <TierManagement />}
      </motion.div>
    </RevolutProvider>
  );
};

export default BillingAndPlanSettings;
